import { ErrorOutline } from '@mui/icons-material';
import {
	Autocomplete,
	Box,
	TextField,
	Typography
} from '@mui/material';
import React, { memo, useCallback } from 'react';

import { labelSx } from 'constants/ui';
import { LocalePath } from 'models';
import { FormattedMessage, useIntl } from 'react-intl';

interface PageFormFieldsProps {
	localePaths: LocalePath[];
	pathError: string | null;
	localePathErrors: Record<string, string | null>;
	onPathChange: (idx: number, value: string) => void;
	pageAlias: string;
	aliasError: string | null;
	pageId: string;
	pageIdOptions: string[];
	onPageIdChange: (value: string) => void;
	/**
	 * Mirrors legacy canEditNavigation (composer-ui/AddPageWindow.js).
	 * Gates the Page Alias and Page ID fields. When false, only page-path
	 * fields are shown.
	 */
	canEditNavigation?: boolean;
}

const PageFormFields = ({
	localePaths,
	pathError,
	localePathErrors,
	onPathChange,
	pageAlias,
	aliasError,
	pageId,
	pageIdOptions,
	onPageIdChange,
	canEditNavigation = true
}: PageFormFieldsProps) => {
	const intl = useIntl();

	const filterOptions = useCallback(
		(options: string[], params: { inputValue: string }) => {
			const { inputValue } = params;
			if (!inputValue || inputValue === pageId) return options;
			return options.filter(id =>
				id.toLowerCase().includes(inputValue.toLowerCase())
			);
		},
		[pageId]
	);

	return (
		<>
			{/* Page Path(s) – one field per locale when multi-language */}
			{localePaths.map(({ locale, path }, idx) => {
				const isFirst = idx === 0;
				const labelSuffix =
					localePaths.length > 1 ? ` (${locale})` : '';

				// Use `in` check so an explicit null (path cleared/valid) doesn't
				// fall through to pathError (which may belong to a different locale)
				let localeError: string | null = null;
				if (locale in localePathErrors) {
					localeError = localePathErrors[locale];
				} else if (isFirst) {
					localeError = pathError;
				}

				return (
					<Box key={locale} sx={{ mt: isFirst ? 0 : 2 }}>
						<Typography variant="body2" sx={{ ...labelSx, mb: 0.5 }}>
							<FormattedMessage id="PAGE_PATH" />
							{labelSuffix}:
						</Typography>
						<TextField
							fullWidth
							value={path}
							onChange={e => onPathChange(idx, e.target.value)}
							size="small"
							error={!!localeError}
							helperText={
								localeError ? (
									<Box
										component="span"
										sx={{
											display: 'flex',
											alignItems: 'center',
											gap: 0.5
										}}
									>
										<ErrorOutline sx={{ fontSize: 14 }} />
										{localeError}
									</Box>
								) : null
							}
							sx={{
								'& input': {
									textOverflow: 'ellipsis',
									overflow: 'hidden',
									whiteSpace: 'nowrap'
								}
							}}
						/>
					</Box>
				);
			})}

			{/* Page Alias – hidden when user lacks COMPOSER_EDIT_NAVIGATION_PRIVILEGED */}
			{canEditNavigation && <Box sx={{ mt: 2 }}>
				<Typography variant="body2" sx={{ ...labelSx, mb: 0.5 }}>
					<FormattedMessage id="PAGE_ALIAS" />:
				</Typography>
				<TextField
					fullWidth
					value={pageAlias}
					size="small"
					error={!!aliasError}
					helperText={aliasError}
					InputProps={{
						readOnly: true,
						sx: {
							'& input': {
								textOverflow: 'ellipsis',
								overflow: 'hidden',
								whiteSpace: 'nowrap'
							}
						}
					}}
				/>
			</Box>}

			{canEditNavigation && <Box sx={{ mt: 2 }}>
				<Typography variant="body2" sx={{ ...labelSx, mb: 0.5 }}>
					<FormattedMessage id="PAGE_ID" />:
				</Typography>
				<Autocomplete
					disableClearable
					disablePortal
					options={pageIdOptions}
					value={pageId || null}
					onChange={(_e, newValue) => {
						onPageIdChange(newValue ?? '');
					}}
					filterOptions={filterOptions}
					noOptionsText={intl.formatMessage({
						id: 'NO_ITEMS_MATCH',
						defaultMessage: 'No item matches'
					})}
					openOnFocus
					ListboxProps={{ style: { maxHeight: 280 } }}
					componentsProps={{
						paper: {
							sx: {
								fontSize: 14,
								minWidth: 330,
								'& .MuiAutocomplete-option': {
									fontSize: 14,
									minHeight: 32,
									py: 0.5,
									whiteSpace: 'normal',
									wordBreak: 'break-word'
								}
							}
						}
					}}
					renderInput={params => (
						<TextField
							{...params}
							size="small"
							placeholder={intl.formatMessage({
								id: 'SELECT_PAGE_ID',
								defaultMessage: 'Select PageId'
							})}
							error={!pageId}
							onKeyDown={e => {
								// Prevent Enter from bubbling to a parent <form> and submitting
								if (e.key === 'Enter') e.stopPropagation();
							}}
						/>
					)}
					sx={{ '& .MuiAutocomplete-inputRoot': { height: 40 } }}
				/>
				{!pageId && (
					<Box
						sx={{
							display: 'flex',
							alignItems: 'center',
							gap: 0.5,
							mt: 1
						}}
					>
						<ErrorOutline sx={{ fontSize: 14, color: 'error.main' }} />
						<Typography
							variant="caption"
							color="error.main"
							sx={{ letterSpacing: 'normal', lineHeight: 'normal' }}
						>
							<FormattedMessage id="PAGE_ID_MISSING_ERROR" />
						</Typography>
					</Box>
				)}
			</Box>}
		</>
	);
};

export default memo(PageFormFields);
